import type { ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { cn } from '../../lib/utils';

export type NoticeTone = 'success' | 'error' | 'warn' | 'info';

const toneStyles: Record<NoticeTone, { box: string; Icon: typeof Info }> = {
  success: {
    box: 'border-[rgb(var(--pos)/0.35)] bg-[rgb(var(--pos)/0.1)] text-[rgb(var(--pos))]',
    Icon: CheckCircle2,
  },
  error: {
    box: 'border-red-500/30 bg-red-500/10 text-red-600',
    Icon: XCircle,
  },
  warn: {
    box: 'border-[rgb(var(--warn)/0.35)] bg-[rgb(var(--warn)/0.1)] text-[rgb(var(--warn))]',
    Icon: AlertTriangle,
  },
  info: {
    box: 'border-[rgb(var(--info)/0.35)] bg-[rgb(var(--info)/0.1)] text-[rgb(var(--info))]',
    Icon: Info,
  },
};

interface InlineNoticeProps {
  tone?: NoticeTone;
  children: ReactNode;
  onDismiss?: () => void;
  className?: string;
}

/** Inline feedback banner (the app never mounts ui/Toast, so use this instead). */
export default function InlineNotice({ tone = 'info', children, onDismiss, className }: InlineNoticeProps) {
  const { box, Icon } = toneStyles[tone];
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={cn('flex items-start gap-2 rounded-lg border p-3 text-sm', box, className)}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div className="flex-1 text-glass-text">{children}</div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="rounded p-0.5 text-glass-text-3 transition hover:bg-glass-3 hover:text-glass-text"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
